import { Controller } from '@hotwired/stimulus'

type AddressController = {
  getAddressState: () => {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }
}

export default class extends Controller {
  static targets = ['container', 'input', 'checkbox']
  static outlets = ['payments--stripe--address-component']

  declare containerTarget: HTMLDivElement
  declare inputTarget: HTMLTextAreaElement
  declare checkboxTarget: HTMLInputElement
  declare hasPaymentsStripeAddressComponentOutlet: boolean
  declare paymentsStripeAddressComponentOutlet: AddressController

  private electionBuddyDetailsPerCountry = {
    CA: {
      name: "ElectionBuddy Inc.",
      address: "9622 42 Avenue #210 Edmonton AB CA",
      taxId: "865070783"
    },
    US: {
      name: "ElectionBuddy Inc.",
      address: "5301 S Superstition Mtn. Dr. Suite 104 #137 Gold Canyon AZ US",
      taxId: "98-1050820"
    }
  }

  connect() {
    console.log('[BillingDetailsComponent] Controller connected')
    this.attachFormListener()
  }

  disconnect() {
    // Listener will be garbage collected with the form
  }

  private attachFormListener() {
    // Find parent form and listen for submit
    const form = this.element.closest('form')
    if (!form) {
      console.warn('[BillingDetailsComponent] Parent form not found')
      return
    }

    form.addEventListener('submit', () => {
      console.log('[BillingDetailsComponent] Form submitting, preparing billing details')
      this.prepareBillingDetailsForSubmit()
    })
  }

  private prepareBillingDetailsForSubmit() {
    // This runs when form is submitted
    // Append ElectionBuddy details if checkbox is checked
    if (!this.checkboxTarget.checked) {
      this.inputTarget.value = ''
      return
    }

    const currentValue = this.inputTarget.value
    const ebDetails = this.obtainElectionBuddyDetails()

    // Append ElectionBuddy details if not already there
    if (currentValue && !currentValue.includes("ElectionBuddy Inc.")) {
      this.inputTarget.value = `${currentValue}${ebDetails}`
    } else if (!currentValue) {
      this.inputTarget.value = ebDetails
    }
  }

  toggleBillingDetails(): void {
    this.containerTarget.classList.toggle('hide')

    // When unchecking, clear the textarea
    if (this.containerTarget.classList.contains('hide')) {
      this.inputTarget.value = ''
      return
    }

    // When checking, pre-fill with address + tax ID
    this.populateBillingDetailsWithAddressAndTaxId()
  }

  handleTaxIdChange(): void {
    // Only update if the checkbox is checked
    if (this.checkboxTarget.checked && !this.containerTarget.classList.contains('hide')) {
      this.populateBillingDetailsWithAddressAndTaxId()
    }
  }

  handleAddressChange(): void {
    // Update billing details if checkbox is checked
    if (this.checkboxTarget.checked && !this.containerTarget.classList.contains('hide')) {
      this.populateBillingDetailsWithAddressAndTaxId()
    }
  }

  prepareBillingDetails(): void {
    // Deprecated - now handled automatically via form submit listener
    // This method is kept for backward compatibility only
    this.prepareBillingDetailsForSubmit()
  }

  private populateBillingDetailsWithAddressAndTaxId(): void {
    const addressLines = this.buildAddressString()
    const taxIdLine = this.getTaxIdLine()

    let content = ''
    if (addressLines) content += addressLines
    if (taxIdLine) content += (content ? '\n' : '') + taxIdLine

    this.inputTarget.value = content
  }

  private buildAddressString(): string {
    if (!this.hasPaymentsStripeAddressComponentOutlet) {
      console.log('[BillingDetails] No address component outlet available')
      return ''
    }

    const addressState = this.paymentsStripeAddressComponentOutlet.getAddressState()
    console.log('[BillingDetails] Building address from state:', addressState)
    const parts: string[] = []

    if (addressState.line1) parts.push(addressState.line1)
    if (addressState.line2) parts.push(addressState.line2)

    if (addressState.city || addressState.state || addressState.postal_code) {
      const cityStateZip = [addressState.city, addressState.state, addressState.postal_code]
        .filter(Boolean)
        .join(', ')
      if (cityStateZip) parts.push(cityStateZip)
    }

    if (addressState.country) parts.push(addressState.country)

    const result = parts.join('\n')
    console.log('[BillingDetails] Built address string:', result)
    return result
  }

  private getTaxIdLine(): string {
    const taxIdElement = document.getElementById('tax_id') as HTMLInputElement | null
    const taxId = taxIdElement?.value?.trim()
    return taxId ? `Tax ID: ${taxId}` : ''
  }

  private obtainElectionBuddyDetails(): string {
    const countryCodeElement = document.getElementById('country_code') as HTMLInputElement | null
    const countryCode = countryCodeElement?.value || 'CA'
    const details = this.electionBuddyDetailsPerCountry[countryCode] || this.electionBuddyDetailsPerCountry.CA

    return `\n${details.name}\n${details.address}\nTax ID: ${details.taxId}`
  }
}
